import React, { useEffect, useState } from "react";
import {
  ClockCircleOutlined,
  ReconciliationFilled,
  UserOutlined,
} from "@ant-design/icons";
import { getListeNormeISA } from "../../../data/normes";
import { DOWNLOAD_FILE_CLIENT } from "../../../api/apiRoutes";
import {
  Button,
  Col,
  DatePicker,
  Input,
  notification,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import moment from "moment/moment";
import TableTitle from "../../../components/common/TableTitle";
import {
  FormContainer,
  TagItem,
} from "../../../components/common/common.components";
import { appTheme } from "../../../config/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import FormLabel from "../../../components/common/Form/FormLabel";
import SearchRow from "../../../components/common/SearchRow";
import PageLabel from "../../../components/common/PageLabel/PageLabel";

const NormesISAAll = () => {
  const [loading, setLoading] = useState(false);
  const [filtersItems, setFiltersItems] = useState([]);
  const [normes, setNormes] = useState([]);

  const [paramsRecherche, setParamsRecherche] = useState({
    infoFile: "",
    createdAt: null,
  });

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 100,
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setNormes([]);
    }
  };

  const fetchDocuments = async () => {
    try {
      const { result } = await getListeNormeISA();
      console.log("🚀 ~ fetchDocuments ~ result:", result);
      if (result) {
        setNormes(result);
        setFiltersItems(result);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des normes :", error);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const downloadDocumentsClient = async (fichier, libelle) => {
    setLoading(true);
    /*  setTimeout(() => {
      window.open(`${DOWNLOAD_FILE_CLIENT}/NORMES/${fichier}`, "_blank");
      setLoading(false);
      notification.success({
        description: "Télécharger avec succès",
      });
    }, 1500); */

    try {
      const fileUrl = `${DOWNLOAD_FILE_CLIENT}/NORMES/${encodeURIComponent(
        fichier
      )}`;

      const response = await fetch(fileUrl, { method: "HEAD" });
      if (!response.ok) {
        setLoading(false);
        if (response.status === 404) {
          throw new Error("Fichier introuvable");
        } else if (response.status === 403) {
          throw new Error("Accès interdit");
        } else {
          throw new Error(`Erreur serveur (${response.status})`);
        }
      }

      const extension = fichier.split(".").pop();
      const filename = libelle.slice(0, libelle.lastIndexOf("."));

      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.setAttribute("download", `${filename}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du téléchargement :", error);
      notification.error({
        description: error.message,
      });
    }
  };

  const columns = [
    {
      title: () => <TableTitle>#</TableTitle>,
      render: (_, record, index) => <>{index + 1}</>,
      width: 40,
    },
    {
      title: () => <TableTitle>Libellé</TableTitle>,
      dataIndex: "libelle",
      render: (_, record) => {
        // Remove ".pdf" from libelle if present
        const libelleSansPdf = record.libelle?.replace(/\.pdf$/i, "");
        return (
          <div className="d-flex align-items-center">
            <span className="svg-icon svg-icon-2x svg-icon-primary me-4">
              <ReconciliationFilled className="text-primary me-2 fs-6" />
            </span>
            <div
              style={{
                fontFamily: "Oswald, sans-serif",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              {libelleSansPdf}
            </div>
          </div>
        );
      },
    },
    {
      title: () => <TableTitle>Description</TableTitle>,
      dataIndex: "description",
      //width: 500,
      render: (_, record) => {
        return (
          <div className="d-flex align-items-center">{record.description}</div>
        );
      },
    },
    {
      title: () => <TableTitle>Code Sommaire</TableTitle>,
      dataIndex: "codeSommaire",
      //width: 150,
      render: (_, record) => {
        return (
          <div className="d-flex align-items-center">
            {record?.codeSommaire ? (
              <Tag bordered={false} color="cyan">
                {record.codeSommaire}
              </Tag>
            ) : (
              <em>N/A</em>
            )}
          </div>
        );
      },
    },
    {
      title: () => <TableTitle>Date d'ajout</TableTitle>,
      dataIndex: "modif",
      width: 150,
      render: (_, record) => (
        <div className="d-flex flex-column align-center">
          <span
            className="chart-blue display-block f-size-12"
            style={{ fontWeight: 600 }}
          >
            <ClockCircleOutlined style={{ fontSize: "12px" }} />{" "}
            {moment(record.createdAt).format("DD/MM/YYYY à HH:mm")}
          </span>
        </div>
      ),
    },
    {
      title: () => <TableTitle>Action</TableTitle>,
      dataIndex: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip
            color={appTheme.colors.primary}
            title="Téléchager le fichier"
          >
            <Button
              shape="circle"
              size={"middle"}
              style={{ backgroundColor: appTheme.colors.primary }}
              onClick={() =>
                downloadDocumentsClient(record.fichier, record.libelle)
              }
              icon={<FontAwesomeIcon icon={faDownload} color={"white"} />}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSearch = () => {
    setLoading(true);

    const searchTerm = paramsRecherche?.infoFile?.toLowerCase().trim() || "";
    const createdAt = paramsRecherche.createdAt;

    const filteredClients = normes.filter((file) => {
      const libelleMatch = file.libelle?.toLowerCase().includes(searchTerm);
      const descriptionMatch = file.description
        ?.toLowerCase()
        .includes(searchTerm);

      const createdAtMatch =
        !createdAt ||
        (file.createdAt && moment(file.createdAt).isSame(createdAt, "day"));

      return (libelleMatch || descriptionMatch) && createdAtMatch;
    });

    setFiltersItems(filteredClients);
    setLoading(false);
  };

  const handleReset = () => {
    setLoading(true);
    setParamsRecherche({
      infoFile: "",
      createdAt: "",
    });
    setFiltersItems(normes);
    setLoading(false);
  };

  return (
    <>
      <div className="w-100">
        <PageLabel label="Liste des normes" />
        <SearchRow
          handleSearch={handleSearch}
          handleReset={handleReset}
          loading={loading}
        >
          <Col
            xs={{ span: 24 }}
            md={{ span: 12 }}
            lg={{ span: 8, offset: 5 }}
            xl={{ span: 8, offset: 5 }}
          >
            <FormLabel label="Fichier" />
            <Input
              style={{ width: "100%" }}
              size="large"
              placeholder="Référence, libellé ou description"
              value={paramsRecherche.infoFile || null}
              allowClear
              onChange={(e) =>
                setParamsRecherche({
                  ...paramsRecherche,
                  infoFile: e.target.value,
                })
              }
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            md={{ span: 12 }}
            lg={{ span: 6 }}
            xl={{ span: 6 }}
          >
            <FormLabel label="Date de mise à jour" />
            <DatePicker
              size="large"
              placeholder="Date de mise à jour"
              style={{ width: "100%" }}
              allowClear
              onChange={(date) =>
                date !== null
                  ? setParamsRecherche({
                      ...paramsRecherche,
                      createdAt: date.format("YYYY-MM-DD"),
                    })
                  : setParamsRecherche({ ...paramsRecherche, createdAt: "" })
              }
              format={"DD-MM-YYYY"}
            />
          </Col>
        </SearchRow>
        <FormContainer>
          <div className="w-100">
            <div className="py-4">
              <div className="table-responsive">
                <Table
                  columns={columns}
                  rowKey={(record) => record.id}
                  dataSource={filtersItems}
                  pagination={tableParams.pagination}
                  loading={loading}
                  onChange={handleTableChange}
                  //rowSelection
                  size={"small"}
                />
              </div>
            </div>
          </div>
        </FormContainer>
      </div>
    </>
  );
};

export { NormesISAAll };
